import { type AgentTargetListItem, type AgentTargetListQuery } from '@utazzo/shared';
import { UzTooltip } from '@utazzo/ui';
import { Info } from 'lucide-react';
import React, { useState } from 'react';

import { listAgentTargets } from '../agent-targets.api';
import { AgentTargetFormDrawer } from '../components/AgentTargetFormDrawer';
import { AgentTargetHistoryDialog } from '../components/AgentTargetHistoryDialog';
import {
  useBulkDeleteAgentTargets,
  useAgentTargetsList,
  useDeleteAgentTarget,
} from '../use-agent-targets';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const PERIOD_OPTIONS = [
  { value: '', label: 'All periods' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUAL', label: 'Annual' },
] as const;

const PERIOD_LABELS: Record<string, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUAL: 'Annual',
};

const PERIOD_COLORS: Record<string, string> = {
  MONTHLY:
    'inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700',
  QUARTERLY:
    'inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700',
  ANNUAL:
    'inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700',
};

const STATIC_FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'period',
    label: 'Period',
    options: PERIOD_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

export const AgentTargetsListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const isAdmin = me?.role === 'ADMIN';
  const canDelete = isAdmin;
  const deleteAgentTarget = useDeleteAgentTarget();
  const bulkDeleteMut = useBulkDeleteAgentTargets();
  // Which agent's target-history popup is open (admin-only).
  const [historyFor, setHistoryFor] = useState<{ id: string; name: string } | null>(null);

  const columns: ListScreenColumns<AgentTargetListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<AgentTargetListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'agent',
        header: 'Agent',
        cell: (row) => (
          <span className="flex items-center gap-1.5">
            <span className="font-medium text-zinc-900">{row.user.fullName}</span>
            {isAdmin && (
              <UzTooltip label="Previous targets">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setHistoryFor({ id: row.user.id, name: row.user.fullName });
                  }}
                  className="inline-flex h-5 w-5 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
                  aria-label={`Previous targets for ${row.user.fullName}`}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </UzTooltip>
            )}
          </span>
        ),
      },
      {
        id: 'period',
        header: 'Period',
        cell: (row) => (
          <span className={PERIOD_COLORS[row.period] ?? PERIOD_COLORS['MONTHLY']}>
            {PERIOD_LABELS[row.period] ?? row.period}
          </span>
        ),
      },
      {
        id: 'window',
        header: 'Effective period',
        sortable: true,
        // Effective dating: the active row (periodEnd === null) shows an
        // "Active since <start>" pill; historical rows show their closed
        // <start> - <end> window.
        cell: (row) =>
          row.periodEnd === null ? (
            <span className="inline-flex items-center gap-1.5 text-sm">
              <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                Active
              </span>
              <span className="text-zinc-500">since {row.periodStart}</span>
            </span>
          ) : (
            <span className="text-sm text-zinc-500">
              {row.periodStart} - {row.periodEnd}
            </span>
          ),
      },
      {
        id: 'target',
        header: 'Target',
        sortable: true,
        cell: (row) => <span className="font-medium">{row.targetAmount.toLocaleString()}</span>,
      },
      // `targetLeads` column retired on 2026-06-16 - admins track
      // quotas by amount only. The DB column is kept for historical
      // rows but is no longer surfaced in the list.
    ];
    return cols;
  };

  return (
    <ListScreen<AgentTargetListItem, AgentTargetListQuery>
      title="Agent Targets"
      description="Sales targets assigned to agents per period."
      useList={useAgentTargetsList}
      defaultSort={{ by: 'periodStart', dir: 'desc' }}
      toQuery={(state, base) => {
        const period = state.filters.period;
        return {
          ...base,
          ...(period === 'MONTHLY' ? { period: 'MONTHLY' as const } : {}),
          ...(period === 'QUARTERLY' ? { period: 'QUARTERLY' as const } : {}),
          ...(period === 'ANNUAL' ? { period: 'ANNUAL' as const } : {}),
        };
      }}
      columns={columns}
      filters={STATIC_FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add target' }}
      overlays={
        historyFor && (
          <AgentTargetHistoryDialog
            userId={historyFor.id}
            userName={historyFor.name}
            open={historyFor !== null}
            onClose={() => setHistoryFor(null)}
          />
        )
      }
      renderDrawer={({ open, mode, row, close }) => (
        <AgentTargetFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          agentTarget={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} agent target${rows.length === 1 ? '' : 's'}?`,
            description: 'These targets will be soft-deleted.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listAgentTargets(q),
        filename: 'agent-targets',
        csv: [
          { header: 'Agent', value: (r) => r.user.fullName },
          { header: 'Period', value: (r) => r.period },
          { header: 'Period start', value: (r) => r.periodStart },
          { header: 'Period end', value: (r) => r.periodEnd ?? '' },
          { header: 'Target amount', value: (r) => r.targetAmount },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteAgentTarget.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this agent target?',
            description: `The ${PERIOD_LABELS[row.period] ?? row.period} target for ${row.user.fullName} starting ${row.periodStart} will be soft-deleted.`,
          }),
        },
      }}
      copy={{
        emptyTitle: 'No agent targets match these filters',
        emptyDescription: 'Adjust the period filter or add the first target.',
        errorTitle: 'Failed to load agent targets',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
